import Honeybadger from 'honeybadger-js';
import Icon from '@xo-union/tk-component-icons';
import React, { MouseEvent, useState } from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import { PhotoItemsFragment, useDeletePhotoItemMutation, useUpdatePhotoItemMutation } from '../../../generated/graphql';
import deleteGalleryPhoto from '../../../helpers/apolloCache/deleteGalleryPhoto';
import updateGalleryPhoto from '../../../helpers/apolloCache/updateGalleryPhoto';
import { imageUrlToImgElement } from '../../../helpers/images/processImages';
import { resizeImageW } from '../../../helpers/mediaApiUrls';
import { pagesQuery } from '../../LeftContainer/graphql';
import PhotoCropper from '../../Modals/PhotoCropper';
import { CropProps, imageFileTypesWithoutGif } from '../ImageField';
import { ReactComponent as GrabHandle } from './GrabHandle.svg';
import styles from './grid.scss';

const SortHandle = SortableHandle(() => (
  <div className={styles.grabHandle}>
    <GrabHandle />
  </div>
));

interface ImageSquareProps {
  photoItem: PhotoItemsFragment;
  imageIndex: number;
  onClick(event: MouseEvent<HTMLDivElement>): void;
}
const ImageSquare = SortableElement(({ imageIndex, onClick, photoItem }: ImageSquareProps) => (
  <div className={styles.imageSquare} onClick={onClick} data-testid={`gallery-item-${imageIndex}`}>
    <SortHandle />
    <div className={styles.imageOverlay}>
      <Icon name="edit" size="md" className={styles.editIcon} />
    </div>
    <img alt={`Gallery Item ${imageIndex + 1}`} src={resizeImageW(photoItem.mediaUrl, 220)} className={styles.image} />
  </div>
));

interface PhotoGalleryGridProps {
  addUploadsOnChange(event: React.ChangeEvent<HTMLInputElement>): void;
  pageId: number;
  photoGalleryId: number | null;
  photoItems: PhotoItemsFragment[];
}

const newImg = new Image(400, 400);
newImg.src = `${process.env.PUBLIC_URL}/assets/img-broken.svg`;
newImg.crossOrigin = 'anonymous';

const PhotoGalleryGrid = SortableContainer((props: PhotoGalleryGridProps) => {
  const [updatePhoto] = useUpdatePhotoItemMutation();
  const [deletePhoto] = useDeletePhotoItemMutation();
  const [cropperPhoto, setCropperPhoto] = useState<PhotoItemsFragment & { img: HTMLImageElement }>();

  const { addUploadsOnChange, pageId, photoGalleryId, photoItems } = props;

  const openCropper = async (photoItem: PhotoItemsFragment) => {
    setCropperPhoto({
      ...photoItem,
      img: await imageUrlToImgElement(photoItem.mediaUrl).catch(error => {
        Honeybadger.notify(error, 'Image missing in the media api.', {
          itemId: photoItem.id,
          pageId,
          Component: 'Grid',
        });

        return newImg;
      }),
    });
  };
  const closeCropper = () => {
    setCropperPhoto(undefined);
  };
  const saveCrop = async ({ crop, rotation }: CropProps) => {
    if (!cropperPhoto) return;

    const variables = {
      attributes: {
        pageId,
        id: cropperPhoto.id,
        height: crop.height || 0,
        width: crop.width || 0,
        cropX: crop.x,
        cropY: crop.y,
        rotation,
      },
    };

    try {
      await updatePhoto({
        variables,
        update(cache, { data }) {
          const updates = data?.updatePhotoItem;
          if (photoGalleryId && updates) {
            updateGalleryPhoto({ cache, pageId, photoGalleryId, photoId: cropperPhoto.id, updates });
          }
        },
      });
    } catch (error) {
      Honeybadger.notify(error, 'Error deleting page item photo', {
        itemId: cropperPhoto.id,
        pageId,
        Component: 'Grid',
      });
    }

    closeCropper();
  };
  const deleteCropperPhoto = async () => {
    if (!cropperPhoto || !photoGalleryId) return;

    try {
      await deletePhoto({
        variables: { attributes: { id: cropperPhoto.id, pageId } },
        update(cache) {
          deleteGalleryPhoto({ cache, pageId, photoGalleryId, photoId: cropperPhoto.id });
        },
        refetchQueries: [{ query: pagesQuery }],
      });
    } catch (error) {
      Honeybadger.notify(error, 'Error deleting page item photo', {
        itemId: cropperPhoto.id,
        pageId,
        Component: 'Grid',
      });
    }

    closeCropper();
  };

  return (
    <div className={styles.imageGrid} key="add_photo_square">
      <label id="select_photos" className={styles.addPhotoSquare}>
        <input
          type="file"
          data-testid="photo-gallery-upload"
          multiple
          accept={imageFileTypesWithoutGif.join(',')}
          onChange={addUploadsOnChange}
          style={{ display: 'none' }}
        />
        <Icon name="add_circle" className={styles.addPhotoIcon} />
        <span>Add Photos</span>
      </label>
      {photoItems?.map((photoItem, index) => (
        <ImageSquare
          imageIndex={index}
          index={index}
          key={photoItem.id}
          onClick={() => {
            openCropper(photoItem);
          }}
          photoItem={photoItem}
        />
      ))}
      {cropperPhoto && (
        <PhotoCropper
          cropperInfo={{
            file: cropperPhoto.img,
            onSave: saveCrop,
          }}
          onCancel={closeCropper}
          pageId={pageId}
          itemType="PhotoItem"
          secondaryActionText="Discard Changes"
          primaryActionText="Save Changes"
          currentAspectRatio={cropperPhoto.aspectRatio}
          onDeletePhotoHandler={deleteCropperPhoto}
          defaultLayoutRatioName={cropperPhoto.aspectRatio?.name || '1:1'}
          defaultLayoutRatioValue={cropperPhoto.aspectRatio?.value || 1}
        />
      )}
    </div>
  );
});

export default PhotoGalleryGrid;
